import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import type { WbTemplateCatalogEntry } from '@/lib/api/services/websiteBuilder';

interface TemplateCardProps {
  template: WbTemplateCatalogEntry;
  thumbnailBaseUrl: string;
  isRecommended: boolean;
  onSelect: (template: WbTemplateCatalogEntry) => void;
}

const FALLBACK_GRADIENTS = [
  'from-violet-500 via-purple-600 to-fuchsia-700',
  'from-blue-500 via-indigo-600 to-purple-700',
  'from-emerald-500 via-teal-600 to-cyan-700',
  'from-orange-500 via-rose-600 to-pink-700',
  'from-pink-500 via-fuchsia-600 to-purple-700',
  'from-cyan-500 via-sky-600 to-blue-700',
  'from-amber-500 via-orange-600 to-red-700',
  'from-lime-500 via-green-600 to-emerald-700',
];

function fallbackGradient(id: string): string {
  const seed = id?.charCodeAt(0) ?? 0;
  return FALLBACK_GRADIENTS[seed % FALLBACK_GRADIENTS.length];
}

function fallbackGlyph(name: string): string {
  const first = (name ?? '').trim().charAt(0);
  return first ? first.toUpperCase() : '★';
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  thumbnailBaseUrl,
  isRecommended,
  onSelect,
}) => {
  const hasThumbKey = Boolean(template.thumbnail);
  const [thumbState, setThumbState] = useState<'ok' | 'fallback'>(
    hasThumbKey ? 'ok' : 'fallback',
  );
  const EMPTY_DATA_URI =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  const src = hasThumbKey
    ? `${thumbnailBaseUrl}${template.thumbnail}`
    : EMPTY_DATA_URI;

  const handleSelect = () => onSelect(template);

  const gradient = fallbackGradient(template.id);
  const glyph = fallbackGlyph(template.name);

  return (
    <div
      className="relative flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-lg hover:border-purple-300 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer overflow-hidden group"
      onClick={handleSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      data-testid="wb-template-card"
    >
      {isRecommended && (
        <div
          className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded-full bg-purple-600 text-white text-[10px] font-semibold px-2 py-1 shadow-lg backdrop-blur-sm pointer-events-none max-w-[calc(100%-1rem)]"
          data-testid="wb-template-card-recommended"
        >
          <Sparkles className="h-3 w-3 shrink-0" />
          <span className="truncate">Recommended</span>
        </div>
      )}

      <div
        className={`relative aspect-[16/10] w-full overflow-hidden ${
          thumbState === 'fallback' ? 'bg-gray-50' : 'bg-gray-50'
        }`}
        data-testid="wb-template-card-thumb-wrapper"
        data-thumb-state={thumbState}
      >
        <img
          src={src}
          alt={template.name}
          loading="lazy"
          onError={() => setThumbState('fallback')}
          className={`h-full w-full object-cover transition-all duration-300 ${
            thumbState === 'fallback'
              ? 'opacity-0'
              : 'opacity-100 group-hover:scale-[1.03]'
          }`}
          data-testid="wb-template-card-thumb"
          aria-hidden={thumbState === 'fallback'}
        />

        {thumbState === 'fallback' && (
          <div
            className={`absolute inset-0 bg-gradient-to-br ${gradient} flex items-center justify-center overflow-hidden`}
            data-testid="wb-template-card-thumb-fallback"
            aria-hidden="true"
          >
            <span className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/20 pointer-events-none" />
            <span className="relative text-white/90 font-black tracking-tight text-7xl sm:text-8xl select-none leading-none drop-shadow-md">
              {glyph}
            </span>
            <span className="absolute bottom-2 left-3 text-[11px] font-semibold text-white/80 uppercase tracking-wider">
              {template.category || 'template'}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col p-4 gap-1.5">
        <h3
          className="text-base font-semibold text-gray-900 line-clamp-1"
          title={template.name}
        >
          {template.name}
        </h3>
        <p
          className="text-xs text-gray-500 line-clamp-2 min-h-[2.5em] leading-relaxed"
          title={template.tagline}
        >
          {template.tagline}
        </p>

        {Array.isArray(template.features) && template.features.length > 0 && (
          <div
            className="flex flex-wrap gap-1 mt-1 max-h-[3.5em] overflow-hidden"
            data-testid="wb-template-card-features"
          >
            {template.features.slice(0, 3).map((f) => (
              <span
                key={f}
                className="inline-flex items-center text-[10px] font-medium text-gray-600 bg-gray-100 rounded-full px-2 py-0.5"
              >
                {f.replace(/_/g, ' ')}
              </span>
            ))}
            {template.features.length > 3 && (
              <span className="inline-flex items-center text-[10px] font-medium text-gray-500 bg-gray-50 rounded-full px-2 py-0.5">
                +{template.features.length - 3} more
              </span>
            )}
          </div>
        )}

        <div className="pt-3 mt-auto">
          <Button
            type="button"
            size="sm"
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              handleSelect();
            }}
            data-testid="wb-template-card-cta"
          >
            Use this template
          </Button>
        </div>
      </div>
    </div>
  );
};
